import {
  createReference,
  getReferenceString,
  MedplumClient,
  PatchOperation,
  WithId,
} from "@medplum/core";
import {
  Bundle,
  BundleEntry,
  ClinicalImpression,
  Coverage,
  EpisodeOfCare,
  Organization,
  Patient,
  PlanDefinition,
  Practitioner,
  Reference,
  RelatedPerson,
  ServiceRequest,
} from "@medplum/fhirtypes";
import { LabOrganization } from "@medplum/health-gorilla-core";

import * as Sentry from "@sentry/react";
import { formatISO } from "date-fns";

import { Extensions } from "@/fhir/enums/extensions";
import { IdentifierType, OrganizationRole, V3ActCode } from "@/fhir/enums/fhir-valuesets";
import {
  QuiltedHealthCodeSystems,
  QuiltedHealthUsageContextTypes,
} from "@/fhir/enums/quilted-health";
import { SNOMEDCodes } from "@/fhir/enums/snomed";
import { Systems } from "@/fhir/enums/systems";
import { fetchPregnancyRecordList, upsertLabOrderToPregnancyRecord } from "@/pregnancy";
import {
  getActiveOrganizationId,
  getCodeableConceptCode,
  getCodeableConceptSystem,
  getDisplayStringAsync,
  getResourcesFromBundle,
  joinReferenceString,
  joinTokenString,
  patchWithRetries,
} from "@/utils/medplum";

import {
  AppendEpisodeOfCareReferralResult,
  HealthGorillaLabOrderInternalStatus,
  HealthGorillaLabOrderInternalStatusDisplay,
  LAB_ORDER_RESULTS_NOTE_CODE,
  LAB_ORDER_RESULTS_ORDERED_DATE_SORT,
  LAB_ORDER_STATUS_SORT_GROUPS,
  PerformingLabAccounts,
} from "../types/labs";
import {
  getLabOrderInternalStatus,
  hasValidAddress,
  isDefaultLabOrderResultsView,
  isTerminalLabOrderInternalStatus,
  planLabOrderStatusGroupPages,
  sendLabOrderToHealthGorilla,
} from "../utils/labs";
import { AddLabOrderFormType } from "../zod-schemas/labs";

type LabOrderResultsQuery = Record<string, string | number | undefined>;

interface LabOrderResultsFilterOptions {
  searchParams?: URLSearchParams;
  practiceId?: string;
  encounterId?: string;
  clientId?: string;
}

/**
 * Builds the resource-level constraints a lab orders query carries: which lab orders the list is
 * about, before any decision about which slice of them to show.
 *
 * Kept apart from the page window so the status-grouped fetch can reuse the constraints across the
 * several queries it runs, each over its own slice.
 *
 * @param options - The list's subject — a practice, client or encounter — and its status filter.
 */
function buildLabOrderResultsFilters({
  searchParams,
  practiceId,
  encounterId,
  clientId,
}: LabOrderResultsFilterOptions): LabOrderResultsQuery {
  const category = joinTokenString(Systems.SNOMED, SNOMEDCodes.LaboratoryProcedure);
  const activeTab = searchParams?.get("status") || HealthGorillaLabOrderInternalStatus.All;
  const statusFilter =
    activeTab === HealthGorillaLabOrderInternalStatus.All
      ? ""
      : ` and category eq ${joinTokenString(
          QuiltedHealthCodeSystems.HealthGorillaLabOrderInternalStatus,
          activeTab,
        )}`;

  const query: LabOrderResultsQuery = {
    _total: "accurate",
    _filter: `category eq ${category}${statusFilter}`,
    "status:not": "entered-in-error",
  };

  if (practiceId) {
    query._compartment = joinReferenceString("Organization", practiceId);
  }

  if (clientId) {
    query.subject = joinReferenceString("Patient", clientId);
  }

  if (encounterId) {
    query.encounter = joinReferenceString("Encounter", encounterId);
  }

  return query;
}

/**
 * Builds one page of a lab orders query, sorted the way the user asked for.
 *
 * @param currentPage - 1-based page being displayed.
 * @param pageSize - Rows per page.
 * @param options - The list's filters, plus the sort to use when the user has picked none.
 */
function buildLabOrderResultsQuery(
  currentPage: number,
  pageSize: number,
  {
    defaultSort = "-_lastUpdated",
    ...filters
  }: LabOrderResultsFilterOptions & { defaultSort?: string },
): LabOrderResultsQuery {
  return {
    ...buildLabOrderResultsFilters(filters),
    _count: pageSize,
    _offset: (currentPage - 1) * pageSize,
    _sort: filters.searchParams?.get("sort") || defaultSort,
  };
}

/**
 * Resolves an EpisodeOfCare suitable for optimistic concurrency patching.
 * Re-reads from the server when the caller only has a partial resource (e.g. form selection without meta).
 */
async function resolveEpisodeOfCareForPatch(
  medplum: MedplumClient,
  episodeOfCare: EpisodeOfCare,
): Promise<
  | { ok: true; episode: EpisodeOfCare }
  | { ok: false; reason: "missing_episode_of_care_id" | "missing_version_id"; details?: unknown }
> {
  if (!episodeOfCare.id) {
    return { ok: false, reason: "missing_episode_of_care_id" };
  }

  if (episodeOfCare.meta?.versionId) {
    return { ok: true, episode: episodeOfCare };
  }

  try {
    const freshEpisode = await medplum.readResource("EpisodeOfCare", episodeOfCare.id);
    if (!freshEpisode.meta?.versionId) {
      return { ok: false, reason: "missing_version_id" };
    }
    return { ok: true, episode: freshEpisode };
  } catch (error) {
    return { ok: false, reason: "missing_version_id", details: error };
  }
}

/**
 * Appends a ServiceRequest reference to `EpisodeOfCare.referralRequest` when absent.
 */
export async function appendServiceRequestToEpisodeOfCareReferrals(
  medplum: MedplumClient,
  episodeOfCare: EpisodeOfCare,
  serviceRequest: ServiceRequest,
): Promise<AppendEpisodeOfCareReferralResult> {
  if (!serviceRequest.id) {
    return { ok: false, reason: "missing_service_request_id" };
  }

  const resolvedEpisode = await resolveEpisodeOfCareForPatch(medplum, episodeOfCare);
  if (!resolvedEpisode.ok) {
    return { ok: false, reason: resolvedEpisode.reason, details: resolvedEpisode.details };
  }

  const episode = resolvedEpisode.episode;
  const episodeId = episode.id!;

  const referenceString =
    getReferenceString(serviceRequest) ?? joinReferenceString("ServiceRequest", serviceRequest.id);
  if (!referenceString) {
    return { ok: false, reason: "missing_reference" };
  }

  const existingReferrals = episode.referralRequest || [];
  const serviceRequestReference = { reference: referenceString };

  if (existingReferrals.some((ref) => ref.reference === serviceRequestReference.reference)) {
    return { ok: true, alreadyLinked: true };
  }

  try {
    await patchWithRetries(medplum, "EpisodeOfCare", episodeId, [
      {
        op: "test",
        path: "/meta/versionId",
        value: episode.meta!.versionId!,
      },
      {
        op: existingReferrals.length ? "replace" : "add",
        path: "/referralRequest",
        value: [...existingReferrals, serviceRequestReference],
      },
    ]);
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: "patch_failed", details: error };
  }
}

/**
 * Searches one status group for a slice of the page on screen.
 *
 * A lab order's internal status lives in `ServiceRequest.category`, alongside the SNOMED category
 * the filters already constrain, so the group's statuses go on the `category` search param and AND
 * with the base `_filter`. They share one comma separated value, which matches any of them.
 *
 * @param medplum - Medplum client.
 * @param filters - The list's resource-level constraints, shared by every group.
 * @param statuses - The internal statuses making up the group.
 * @param slice - Rows to skip and take within the group.
 */
async function searchLabOrderStatusGroup(
  medplum: MedplumClient,
  filters: LabOrderResultsQuery,
  statuses: readonly HealthGorillaLabOrderInternalStatus[],
  { offset, count }: { offset: number; count: number },
) {
  const query: LabOrderResultsQuery = {
    ...filters,
    _count: count,
    _offset: offset,
    _sort: LAB_ORDER_RESULTS_ORDERED_DATE_SORT,
    category: statuses
      .map((status) =>
        joinTokenString(QuiltedHealthCodeSystems.HealthGorillaLabOrderInternalStatus, status),
      )
      .join(","),
  };

  const { bundle } = await medplum.searchResources("ServiceRequest", query, {
    cache: "no-cache",
  });

  return bundle;
}

/**
 * Counts the lab orders in one status group without fetching any of them.
 *
 * @param medplum - Medplum client.
 * @param filters - The list's resource-level constraints, shared by every group.
 * @param statuses - The internal statuses making up the group.
 * @returns How many lab orders the group holds.
 */
async function countLabOrderStatusGroup(
  medplum: MedplumClient,
  filters: LabOrderResultsQuery,
  statuses: readonly HealthGorillaLabOrderInternalStatus[],
): Promise<number> {
  const bundle = await searchLabOrderStatusGroup(medplum, filters, statuses, {
    offset: 0,
    count: 0,
  });

  return bundle.total ?? 0;
}

/**
 * Fetches one page of a practice's lab orders ordered by status group, then by ordered date.
 *
 * Status priority is not something FHIR can sort on — it is a coding inside `category` — so each
 * group is queried on its own and the groups are read back to back as one list. Counting every
 * group first is what lets a page start part way through a group: the counts place the page window
 * over the groups, and only the groups it lands on are fetched.
 *
 * @param medplum - Medplum client.
 * @param practiceId - Practice whose lab orders to list.
 * @param currentPage - 1-based page being displayed.
 * @param pageSize - Rows per page.
 * @returns The page's lab orders in status order, and the practice's total across all groups.
 */
async function fetchLabOrderResultsByStatusGroup(
  medplum: MedplumClient,
  practiceId: string,
  currentPage: number,
  pageSize: number,
) {
  const filters = buildLabOrderResultsFilters({ practiceId });

  const counts = await Promise.all(
    LAB_ORDER_STATUS_SORT_GROUPS.map((statuses) =>
      countLabOrderStatusGroup(medplum, filters, statuses),
    ),
  );

  const total = counts.reduce((sum, count) => sum + count, 0);
  const slices = planLabOrderStatusGroupPages(counts, currentPage, pageSize);

  const groups = await Promise.all(
    slices.map(async ({ groupIndex, offset, count }) => {
      const bundle = await searchLabOrderStatusGroup(
        medplum,
        filters,
        LAB_ORDER_STATUS_SORT_GROUPS[groupIndex],
        { offset, count },
      );
      return getResourcesFromBundle("ServiceRequest", bundle);
    }),
  );

  return { results: groups.flat(), total };
}

export async function fetchLabOrderResultsForPractice(
  medplum: MedplumClient,
  practiceId: string,
  currentPage: number,
  pageSize: number,
  searchParams: URLSearchParams,
) {
  if (isDefaultLabOrderResultsView(searchParams)) {
    return fetchLabOrderResultsByStatusGroup(medplum, practiceId, currentPage, pageSize);
  }

  const query = buildLabOrderResultsQuery(currentPage, pageSize, {
    searchParams,
    practiceId,
    defaultSort: LAB_ORDER_RESULTS_ORDERED_DATE_SORT,
  });

  const { bundle } = await medplum.searchResources("ServiceRequest", query, {
    cache: "no-cache",
  });
  const total = bundle.total ?? 0;

  const results = getResourcesFromBundle("ServiceRequest", bundle);

  return { results, total };
}

export async function fetchLabOrderResultsForClient(
  medplum: MedplumClient,
  clientId: string,
  currentPage: number,
  pageSize: number,
  searchParams: URLSearchParams,
) {
  const query = buildLabOrderResultsQuery(currentPage, pageSize, {
    searchParams,
    clientId,
  });
  const { bundle } = await medplum.searchResources("ServiceRequest", query, {
    cache: "no-cache",
  });
  const total = bundle.total ?? 0;

  const results = getResourcesFromBundle("ServiceRequest", bundle);

  return { results, total };
}

export async function fetchLabOrderResultsForEncounter(
  medplum: MedplumClient,
  clientId: string,
  currentPage: number,
  pageSize: number,
  encounterId: string,
) {
  const query = buildLabOrderResultsQuery(currentPage, pageSize, {
    clientId,
    encounterId,
  });
  const { bundle } = await medplum.searchResources("ServiceRequest", query, {
    cache: "no-cache",
  });
  const total = bundle.total ?? 0;

  const results = getResourcesFromBundle("ServiceRequest", bundle);

  return { results, total };
}

export async function fetchLabOrderResultsAsClient(
  medplum: MedplumClient,
  clientId: string,
  currentPage: number,
  pageSize: number,
) {
  const needsReviewStatus = joinTokenString(
    QuiltedHealthCodeSystems.HealthGorillaLabOrderInternalStatus,
    HealthGorillaLabOrderInternalStatus.NeedsReview,
  );
  const reviewedStatus = joinTokenString(
    QuiltedHealthCodeSystems.HealthGorillaLabOrderInternalStatus,
    HealthGorillaLabOrderInternalStatus.Reviewed,
  );

  const query = buildLabOrderResultsQuery(currentPage, pageSize, {
    clientId,
  });

  query._filter = `${query._filter} and (category eq ${needsReviewStatus} or category eq ${reviewedStatus})`;

  const { bundle } = await medplum.searchResources("ServiceRequest", query, {
    cache: "no-cache",
  });
  const total = bundle.total ?? 0;

  const results = getResourcesFromBundle("ServiceRequest", bundle);

  return { results, total };
}

export async function fetchLabOrderResultsMessage(
  medplum: MedplumClient,
  serviceRequest: ServiceRequest,
) {
  return (
    (
      await medplum.searchOne("ClinicalImpression", {
        subject: getReferenceString(serviceRequest.subject),
        "supporting-info": getReferenceString(serviceRequest),
        _sort: "-_lastUpdated",
      })
    )?.summary ?? ""
  );
}

export async function saveLabOrder(
  medplum: MedplumClient,
  values: AddLabOrderFormType,
  onCreateBundle: () => Promise<{ serviceRequest: ServiceRequest }>,
  canSendLabOrder: boolean,
  patientId: string,
  performingLabPhysicianAccountNumber?: string,
): Promise<ServiceRequest | undefined> {
  let { serviceRequest } = await onCreateBundle();

  try {
    const status: HealthGorillaLabOrderInternalStatus = canSendLabOrder
      ? HealthGorillaLabOrderInternalStatus.Ordered
      : HealthGorillaLabOrderInternalStatus.NeedsApproval;

    serviceRequest = (await updateLabOrderInternalStatus(medplum, serviceRequest, status))!;

    if (serviceRequest.id) {
      const pregnancyLists = await fetchPregnancyRecordList(
        medplum,
        joinReferenceString("Patient", patientId),
        { status: "current" },
      );
      if (pregnancyLists?.[0]) {
        await upsertLabOrderToPregnancyRecord(medplum, pregnancyLists[0], serviceRequest);
      }
    }

    if (values.episodeOfCare && serviceRequest.id) {
      await appendServiceRequestToEpisodeOfCareReferrals(
        medplum,
        values.episodeOfCare,
        serviceRequest,
      );
    }

    if (values.encounterId && serviceRequest.id) {
      serviceRequest = await patchWithRetries(medplum, "ServiceRequest", serviceRequest.id, [
        {
          op: "test",
          path: "/meta/versionId",
          value: serviceRequest.meta!.versionId!,
        },
        {
          op: "add",
          path: "/encounter",
          value: { reference: joinReferenceString("Encounter", values.encounterId) },
        },
      ]);
    }

    if (performingLabPhysicianAccountNumber && serviceRequest.id) {
      serviceRequest = await patchWithRetries(medplum, "ServiceRequest", serviceRequest.id, [
        {
          op: "test",
          path: "/meta/versionId",
          value: serviceRequest.meta!.versionId!,
        },
        {
          // Appends rather than replacing `/extension`, so a retry after a version conflict keeps
          // whatever extension the conflicting write added. A Health Gorilla lab order always has
          // the array — createLabOrderBundle puts `billTo` on every one.
          op: "add",
          path: "/extension/-",
          value: {
            url: Extensions.PerformingLabPhysicianAccountNumber,
            valueString: performingLabPhysicianAccountNumber,
          },
        },
      ]);
    }

    if (canSendLabOrder) {
      serviceRequest = await sendLabOrderToHealthGorilla(medplum, serviceRequest);
    }

    return serviceRequest;
  } catch (error) {
    if (serviceRequest.id) {
      await handleFailedLabOrder(medplum, serviceRequest);
    }
    throw error;
  }
}

async function handleFailedLabOrder(
  medplum: MedplumClient,
  laboratoryProcedure: ServiceRequest,
): Promise<void> {
  if (!laboratoryProcedure.id) return;

  const laboratoryProcedureReference = getReferenceString(laboratoryProcedure) as string;
  const entries: BundleEntry<ServiceRequest>[] = [
    {
      resource: {
        ...laboratoryProcedure,
        status: "entered-in-error",
      },
      request: {
        method: "PUT",
        url: laboratoryProcedureReference,
      },
    },
  ];

  try {
    const { bundle } = await medplum.searchResources("ServiceRequest", {
      "based-on": laboratoryProcedureReference,
      category: joinTokenString(Systems.SNOMED, SNOMEDCodes.DiagnosticProcedure),
    });

    const diagnosticProcedures = getResourcesFromBundle("ServiceRequest", bundle);
    for (const diagnosticProcedure of diagnosticProcedures) {
      entries.push({
        resource: {
          ...diagnosticProcedure,
          status: "entered-in-error",
        },
        request: {
          method: "PUT",
          url: getReferenceString(diagnosticProcedure),
        },
      });
    }
  } catch (error) {
    console.error(error);
  }

  await medplum.executeBatch({
    resourceType: "Bundle",
    type: "transaction",
    entry: entries,
  });
}

export async function approveLabOrder(
  medplum: MedplumClient,
  serviceRequest: ServiceRequest,
): Promise<ServiceRequest | undefined> {
  const updatedServiceRequest = await sendLabOrderToHealthGorilla(medplum, serviceRequest);
  return await updateLabOrderInternalStatus(
    medplum,
    updatedServiceRequest,
    HealthGorillaLabOrderInternalStatus.Ordered,
  );
}

export function validatePerformingLab(
  template: PlanDefinition,
  currentPerformingLab: LabOrganization | undefined,
  accountNumbersByLab: Record<string, string>,
): {
  isPerformingLabValid: boolean;
  performingLabReference: Reference<LabOrganization> | undefined;
} {
  const performingLabReference = template?.useContext?.find((context) => {
    return context.code?.code === QuiltedHealthUsageContextTypes.LabOrderTemplates;
  })?.valueReference;

  if (
    !performingLabReference ||
    !performingLabReference.reference ||
    !accountNumbersByLab[performingLabReference.reference]
  )
    return { isPerformingLabValid: false, performingLabReference: undefined };

  return {
    isPerformingLabValid:
      !currentPerformingLab ||
      getReferenceString(currentPerformingLab) === performingLabReference.reference,
    performingLabReference: performingLabReference as Reference<LabOrganization>,
  };
}

export async function updateLabOrderInternalStatus(
  medplum: MedplumClient,
  serviceRequest: ServiceRequest | undefined,
  status: HealthGorillaLabOrderInternalStatus,
  agent?: Reference<Practitioner | Organization>,
) {
  if (!serviceRequest) return;

  const currentStatus = getLabOrderInternalStatus(serviceRequest);
  if (
    isTerminalLabOrderInternalStatus(currentStatus) &&
    !isTerminalLabOrderInternalStatus(status)
  ) {
    return serviceRequest;
  }

  let who: Reference<Practitioner | Organization>;

  if (!agent) {
    const profile = medplum.getProfile() as Practitioner;
    if (!profile) return serviceRequest;
    who = createReference(profile);
  } else {
    who = agent;
  }

  const codeableConcept = {
    coding: [
      {
        system: QuiltedHealthCodeSystems.HealthGorillaLabOrderInternalStatus,
        code: status,
        display: HealthGorillaLabOrderInternalStatusDisplay[status],
      },
    ],
  };

  await medplum.createResource({
    resourceType: "Provenance",
    target: [createReference(serviceRequest), serviceRequest.subject],
    recorded: formatISO(new Date()),
    agent: [{ who }],
    activity: codeableConcept,
  });

  const operations: PatchOperation[] = [
    {
      op: serviceRequest.category?.length ? "replace" : "add",
      path: "/category",
      value: [
        ...(serviceRequest.category ?? []).filter(
          (category) =>
            category.coding?.[0]?.system !==
            QuiltedHealthCodeSystems.HealthGorillaLabOrderInternalStatus,
        ),
        codeableConcept,
      ],
    },
  ];

  if (
    status === HealthGorillaLabOrderInternalStatus.Cancelled ||
    status === HealthGorillaLabOrderInternalStatus.Denied
  ) {
    operations.push({
      op: "replace",
      path: "/status",
      value: "revoked",
    });
  }

  if (status === HealthGorillaLabOrderInternalStatus.NeedsApproval) {
    operations.push({
      op: "add",
      path: "/authoredOn",
      value: formatISO(new Date()),
    });
  }

  if (status === HealthGorillaLabOrderInternalStatus.NeedsReview) {
    operations.push({
      op: serviceRequest.occurrenceDateTime ? "replace" : "add",
      path: "/occurrenceDateTime",
      value: formatISO(new Date()),
    });
  }

  if (
    status === HealthGorillaLabOrderInternalStatus.Reviewed &&
    !serviceRequest.occurrenceDateTime
  ) {
    operations.push({
      op: "add",
      path: "/occurrenceDateTime",
      value: formatISO(new Date()),
    });
  }

  const versionId = serviceRequest.meta?.versionId;
  if (versionId) {
    operations.unshift({
      op: "test",
      path: "/meta/versionId",
      value: versionId,
    });
  }

  return patchWithRetries(medplum, "ServiceRequest", serviceRequest.id!, operations);
}

export async function updateLabOrderResultsNote(
  medplum: MedplumClient,
  serviceRequest: ServiceRequest,
  clientReference: Reference<Patient>,
  note: string,
): Promise<ClinicalImpression | undefined> {
  const profile = medplum.getProfile() as Practitioner;
  if (!profile) return;

  return await medplum.upsertResource(
    {
      resourceType: "ClinicalImpression",
      status: "completed",
      summary: note,
      subject: clientReference,
      assessor: createReference(profile),
      supportingInfo: [createReference(serviceRequest)],
      date: new Date().toISOString(),
      code: {
        coding: [
          {
            system: Systems.QH,
            code: LAB_ORDER_RESULTS_NOTE_CODE,
          },
        ],
      },
    },
    {
      subject: clientReference.reference,
      "supporting-info": getReferenceString(serviceRequest),
    },
  );
}

function hasRelationshipToSubscriber(coverage: Coverage) {
  return (
    !!coverage.relationship?.text?.trim() ||
    !!coverage.relationship?.coding?.some((coding) => coding.code?.trim())
  );
}

export async function fetchCoverages(medplum: MedplumClient, patient: Patient) {
  try {
    const params = {
      beneficiary: getReferenceString(patient),
      status: "active",
      _sort: "-_lastUpdated",
      type: joinTokenString(Systems.V3ActCode, V3ActCode.HealthInsurancePolicy),
      _include: "Coverage:subscriber",
      _count: 100,
    };

    const { bundle }: { bundle: Bundle<WithId<Coverage | RelatedPerson | Patient>> } =
      await medplum.searchResources("Coverage", params);
    const coverages = getResourcesFromBundle<Coverage>("Coverage", bundle as Bundle<Coverage>);
    const relatedPersons = getResourcesFromBundle<RelatedPerson>(
      "RelatedPerson",
      bundle as Bundle<RelatedPerson>,
    ).reduce(
      (acc, relatedPerson) => ({
        ...acc,
        [getReferenceString(relatedPerson)!]: relatedPerson,
      }),
      {} as Record<string, RelatedPerson>,
    );
    const patients = getResourcesFromBundle<Patient>("Patient", bundle as Bundle<Patient>).reduce(
      (acc, patient) => ({
        ...acc,
        [getReferenceString(patient)!]: patient,
      }),
      {} as Record<string, Patient>,
    );
    const sortedCoverages = coverages
      .filter((coverage) => {
        const subscriberReference = coverage.subscriber?.reference;
        // Health Gorilla rejects orders billed to a coverage without a subscriber
        // or without a relationship to the subscriber
        if (!subscriberReference || !hasRelationshipToSubscriber(coverage)) {
          return false;
        }
        // Health Gorilla requires a valid address for the subscriber
        if (
          relatedPersons[subscriberReference] &&
          !hasValidAddress(relatedPersons[subscriberReference].address)
        ) {
          return false;
        }
        if (
          patients[subscriberReference] &&
          !hasValidAddress(patients[subscriberReference].address)
        ) {
          return false;
        }

        return !!coverage.subscriberId?.trim();
      })
      // Sort by order if available, otherwise by date or keep original order
      .sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        if (a.order !== undefined) return -1;
        if (b.order !== undefined) return 1;
        return 0;
      })
      .slice(0, 3);

    const coverageDisplays: Record<string, string> = {};
    for (const coverage of sortedCoverages) {
      let display: string = "";
      if (coverage.payor?.[0]) {
        display = await getDisplayStringAsync(medplum, coverage.payor[0]);
      }
      coverageDisplays[coverage.id!] = display;
    }

    const insuranceList = sortedCoverages
      .map((coverage) => ({
        id: coverage.id!,
        name: coverageDisplays[coverage.id!],
        rank: coverage.order ?? 0,
      }))
      .filter((coverage) => !!coverage.name?.trim());

    return { sortedCoverages, insuranceList };
  } catch (error) {
    console.error("Failed to fetch patient insurances:", error);
    Sentry.captureException(error);
  }

  return { sortedCoverages: [], insuranceList: [] };
}

export async function fetchAvailablePerformingLabAccounts(
  medplum: MedplumClient,
  practiceId: string,
): Promise<PerformingLabAccounts> {
  const { bundle } = await medplum.searchResources("OrganizationAffiliation", {
    "primary-organization": joinReferenceString("Organization", practiceId),
    role: OrganizationRole.Diagnostics,
    active: true,
  });

  const affiliations = getResourcesFromBundle("OrganizationAffiliation", bundle);

  return affiliations.reduce<PerformingLabAccounts>(
    (acc, affiliation) => {
      if (!affiliation.participatingOrganization) return acc;

      const referenceString = getReferenceString(affiliation.participatingOrganization);
      if (!referenceString) return acc;

      const accountNumber = affiliation.identifier?.find(
        (identifier) =>
          getCodeableConceptSystem(identifier.type) === Systems.IdentifierType &&
          getCodeableConceptCode(identifier.type) === IdentifierType.AccountNumber,
      )?.value;
      if (!accountNumber) return acc;

      const physicianAccountNumber = affiliation.identifier?.find(
        (identifier) => identifier.system === Systems.LabCorpPractitionerAccountNumber,
      )?.value;

      return {
        accountNumbersByLab: { ...acc.accountNumbersByLab, [referenceString]: accountNumber },
        physicianAccountNumbersByLab: physicianAccountNumber
          ? { ...acc.physicianAccountNumbersByLab, [referenceString]: physicianAccountNumber }
          : acc.physicianAccountNumbersByLab,
      };
    },
    { accountNumbersByLab: {}, physicianAccountNumbersByLab: {} },
  );
}

export async function fetchIsLabOrdersIntegrationActive(
  medplum: MedplumClient,
  practiceId?: string,
): Promise<boolean> {
  const profile = medplum.getProfile();

  // It doesn't matter for Patients since they can't order labs
  if (profile?.resourceType === "Patient") return true;

  const organizationId = practiceId ?? getActiveOrganizationId();
  if (!organizationId) return false;

  const organizationAffiliation = await medplum.searchOne("OrganizationAffiliation", {
    active: true,
    "primary-organization": joinReferenceString("Organization", organizationId),
    role: joinTokenString(Systems.OrganizationRole, OrganizationRole.Diagnostics),
  });

  return Boolean(organizationAffiliation);
}
